import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import {
  Package, Plus, Trash2, Printer, Filter, Settings, Pill,
  Wrench, ChevronDown, Briefcase, ClipboardCheck, Check, Download, Upload,
  Undo2, AlertTriangle, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

import PageLayout from '../components/PageLayout';

// Sidebar removed - using AppSidebar component

const InventorySystem = () => {
  const { logout } = useAuth();
  const [activeCategory, setActiveCategory] = useState('supplies');
  const [types, setTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Undo state — stores the most recent inline edit
  const [undoState, setUndoState] = useState(null); // { itemId, field, previousValue, itemName }
  
  // Detail modal state
  const [detailItem, setDetailItem] = useState(null);
  const [detailForm, setDetailForm] = useState({});
  const [savingDetail, setSavingDetail] = useState(false);
  
  // Dialogs
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [typeForm, setTypeForm] = useState({ name: '', unit_of_measurement: 'each' });
  const [itemForm, setItemForm] = useState({ type_id: '', name: '', qty_us_warehouse: 0, qty_guatemala: 0, unit_cost: 0 });

  // Sort
  const [inventorySort, setInventorySort] = useState('name-az');

  // Column toggle — persisted in localStorage
  const DEFAULT_COLUMNS = ['name', 'qty_us', 'qty_bags', 'qty_guatemala'];
  const ALL_OPTIONAL_COLUMNS = [
    { key: 'lot_batch', label: 'Lot/Batch #' },
    { key: 'expiration_date', label: 'Expiration' },
    { key: 'country_of_origin', label: 'Origin' },
    { key: 'controlled_substance', label: 'Controlled' },
    { key: 'unit_cost', label: 'Unit Cost' },
    { key: 'par_level', label: 'Par Level' },
  ];
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('inventory_columns');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const toggleColumn = (key) => {
    const next = visibleColumns.includes(key) ? visibleColumns.filter(k => k !== key) : [...visibleColumns, key];
    setVisibleColumns(next);
    try { localStorage.setItem('inventory_columns', JSON.stringify(next)); } catch {}
  };
  const isColumnVisible = (key) => visibleColumns.includes(key);

  // Controlled substance filter
  const [controlledFilter, setControlledFilter] = useState('all');

  // Fetch data
  const fetchTypes = useCallback(async () => {
    try {
      const res = await api.get(`/inventory/types?category=${activeCategory}`);
      setTypes(res.data);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  }, [activeCategory]);

  const fetchItems = useCallback(async () => {
    try {
      let url = `/inventory/items?category=${activeCategory}`;
      if (selectedType !== 'all') {
        url = `/inventory/items?type_id=${selectedType}`;
      }
      const res = await api.get(url);
      setItems(res.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, selectedType]);

  useEffect(() => {
    fetchTypes();
    fetchItems();
  }, [fetchTypes, fetchItems]);

  useEffect(() => {
    setSelectedType('all');
  }, [activeCategory]);

  // Type CRUD
  const handleCreateType = async () => {
    try {
      await api.post('/inventory/types', { ...typeForm, category: activeCategory });
      toast.success('Type created');
      setShowTypeDialog(false);
      setTypeForm({ name: '', unit_of_measurement: 'each' });
      fetchTypes();
    } catch (error) {
      toast.error('Failed to create type');
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!confirm('Delete this type? Items using it will need to be reassigned.')) return;
    try {
      await api.delete(`/inventory/types/${typeId}`);
      toast.success('Type deleted');
      fetchTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete type');
    }
  };

  // Item CRUD
  const handleCreateItem = async () => {
    try {
      await api.post('/inventory/items', itemForm);
      toast.success('Item created');
      setShowItemDialog(false);
      setItemForm({ type_id: '', name: '', qty_us_warehouse: 0, qty_guatemala: 0, unit_cost: 0 });
      fetchItems();
    } catch (error) {
      toast.error('Failed to create item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Delete this item?')) return;
    try {
      await api.delete(`/inventory/items/${itemId}`);
      toast.success('Item deleted');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  // Inline editing
  const startEdit = (itemId, field, value) => {
    setEditingCell({ itemId, field });
    setEditValue(value?.toString() || '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    
    const { itemId, field } = editingCell;
    let value = parseFloat(editValue) || 0;
    
    try {
      const res = await api.put(`/inventory/items/${itemId}/inline`, { [field]: value });
      const itemName = items.find(i => i.item_id === itemId)?.name || '';
      setUndoState({
        itemId,
        field,
        previousValue: res.data.previous?.[field],
        itemName,
      });
      fetchItems();
      cancelEdit();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleUndo = async () => {
    if (!undoState) return;
    try {
      await api.put(`/inventory/items/${undoState.itemId}/inline`, { [undoState.field]: undoState.previousValue });
      toast.success(`Reverted ${undoState.itemName} ${undoState.field.replace(/_/g, ' ')}`);
      setUndoState(null);
      fetchItems();
    } catch {
      toast.error('Failed to undo');
    }
  };

  // Open detail modal
  const openDetail = (item) => {
    setDetailItem(item);
    setDetailForm({
      name: item.name || '',
      type_id: item.type_id || '',
      unit_of_measurement: item.unit_of_measurement || item.unit || '',
      unit_cost: item.unit_cost || 0,
      par_level: item.par_level ?? '',
      supplier_primary_name: item.supplier_primary_name || '',
      supplier_primary_pn: item.supplier_primary_pn || '',
      supplier_secondary1_name: item.supplier_secondary1_name || '',
      supplier_secondary1_pn: item.supplier_secondary1_pn || '',
      supplier_secondary2_name: item.supplier_secondary2_name || '',
      supplier_secondary2_pn: item.supplier_secondary2_pn || '',
      lot_batch: item.lot_batch || '',
      expiration_date: item.expiration_date || '',
      country_of_origin: item.country_of_origin || 'USA',
      controlled_substance: item.controlled_substance || false,
    });
  };

  const saveDetail = async () => {
    if (!detailItem) return;
    setSavingDetail(true);
    try {
      const update = { ...detailForm };
      if (update.par_level === '') update.par_level = null;
      else update.par_level = parseInt(update.par_level) || null;
      update.unit_cost = parseFloat(update.unit_cost) || 0;
      await api.put(`/inventory/items/${detailItem.item_id}`, update);
      toast.success('Item updated');
      setDetailItem(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingDetail(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') cancelEdit();
  };

  // Print sheet
  const openPrintSheet = (typeId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/print-sheet/${activeCategory}/${typeId}`, '_blank');
  };

  // Calculate totals
  const totals = items.reduce((acc, item) => ({
    us: acc.us + (item.qty_us_warehouse || 0),
    inBags: acc.inBags + (item.qty_in_bags || 0),
    guatemala: acc.guatemala + (item.qty_guatemala || 0),
    value: acc.value + ((item.qty_us_warehouse || 0) + (item.qty_in_bags || 0) + (item.qty_guatemala || 0)) * (item.unit_cost || 0)
  }), { us: 0, inBags: 0, guatemala: 0, value: 0 });

  const categoryConfig = {
    supplies: { icon: Package, label: 'Supplies', color: 'bg-blue-500' },
    medications: { icon: Pill, label: 'Medications', color: 'bg-green-500' },
    equipment: { icon: Wrench, label: 'Equipment', color: 'bg-orange-500' }
  };

  const invActions = (
    <>
      <Link to="/admin/bags"><Button variant="outline" size="sm" className="rounded-full h-8 text-xs" data-testid="bags-btn"><Briefcase className="w-3.5 h-3.5 mr-1.5" />Bags</Button></Link>
      <Link to="/admin/count-session"><Button variant="outline" size="sm" className="rounded-full h-8 text-xs" data-testid="count-session-btn"><ClipboardCheck className="w-3.5 h-3.5 mr-1.5" />Count</Button></Link>
      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/inventory/export`} download><Button variant="outline" size="sm" className="rounded-full h-8 text-xs" data-testid="export-inventory-btn"><Download className="w-3.5 h-3.5 mr-1.5" />Export</Button></a>
      <label className="cursor-pointer">
        <input type="file" accept=".csv" className="hidden" data-testid="import-inventory-input"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const formData = new FormData();
            formData.append('file', file);
            try {
              const res = await api.post('/inventory/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
              toast.success(res.data.message);
              if (res.data.errors?.length) res.data.errors.forEach(err => toast.error(err));
              fetchItems();
            } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
            e.target.value = '';
          }}
        />
        <Button variant="outline" size="sm" className="rounded-full h-8 text-xs" asChild data-testid="import-inventory-btn">
          <span><Upload className="w-3.5 h-3.5 mr-1.5" />Import</span>
        </Button>
      </label>
    </>
  );

  return (
    <PageLayout title="Inventory" headerSub="Spreadsheet view — click cells to edit" headerActions={invActions}>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">US Warehouse</p>
                <p className="text-2xl font-bold text-slate-800">{totals.us.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">In Bags</p>
                <p className="text-2xl font-bold text-slate-800">{totals.inBags.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Guatemala</p>
                <p className="text-2xl font-bold text-slate-800">{totals.guatemala.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Value</p>
                <p className="text-2xl font-bold text-slate-800">${totals.value.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Category Tabs */}
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="space-y-4">
            <div className="flex flex-col gap-3">
              <TabsList className="bg-white border border-slate-200 w-full sm:w-auto">
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <TabsTrigger key={key} value={key} className="data-[state=active]:bg-primary data-[state=active]:text-white flex-1 sm:flex-none">
                    <config.icon className="w-4 h-4 mr-1.5 sm:mr-2" />
                    {config.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <div className="flex flex-wrap items-center gap-2">
                {/* Type Filter */}
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="type-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {types.map(t => (
                      <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={inventorySort} onValueChange={setInventorySort}>
                  <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="inventory-sort">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name-az">Name A–Z</SelectItem>
                    <SelectItem value="name-za">Name Z–A</SelectItem>
                    <SelectItem value="us-asc">US Qty: Low–High</SelectItem>
                    <SelectItem value="us-desc">US Qty: High–Low</SelectItem>
                    <SelectItem value="gt-asc">Guatemala Qty: Low–High</SelectItem>
                    <SelectItem value="gt-desc">Guatemala Qty: High–Low</SelectItem>
                    <SelectItem value="exp-asc">Expiration: Soonest</SelectItem>
                    <SelectItem value="exp-desc">Expiration: Latest</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={controlledFilter} onValueChange={setControlledFilter}>
                  <SelectTrigger className="w-full sm:w-[160px] h-9" data-testid="controlled-filter">
                    <SelectValue placeholder="All Items" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Items</SelectItem>
                    <SelectItem value="controlled">Controlled Only</SelectItem>
                    <SelectItem value="non-controlled">Non-Controlled</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9 text-xs" data-testid="column-toggle-btn">
                      Columns
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-48 p-3 space-y-2">
                    {ALL_OPTIONAL_COLUMNS.map(col => (
                      <label key={col.key} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={isColumnVisible(col.key)} onChange={() => toggleColumn(col.key)}
                          className="w-3.5 h-3.5 rounded border-slate-300" />
                        {col.label}
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>

                {/* Manage Types */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full h-9" data-testid="manage-types-btn">
                      <Settings className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Types</span>
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => setShowTypeDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />Add New Type
                    </DropdownMenuItem>
                    {types.map(t => (
                      <DropdownMenuItem key={t.type_id} className="flex justify-between">
                        <span>{t.name}</span>
                        <div className="flex gap-1">
                          <button onClick={(e) => { e.stopPropagation(); openPrintSheet(t.type_id); }} className="p-1 hover:bg-slate-100 rounded">
                            <Printer className="w-3 h-3" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteType(t.type_id); }} className="p-1 hover:bg-red-100 rounded text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Add Item */}
                <Button className="rounded-full bg-rojo hover:bg-rojo-dark h-9 ml-auto sm:ml-0" onClick={() => setShowItemDialog(true)} data-testid="add-item-btn">
                  <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Add Item</span>
                </Button>
              </div>
            </div>

            {/* Spreadsheet Table */}
            {['supplies', 'medications', 'equipment'].map(cat => (
              <TabsContent key={cat} value={cat} className="mt-0">
                <Card className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm min-w-[800px]" data-testid={`${cat}-table`}>
                        <thead className="bg-slate-100 border-b border-slate-200">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600 min-w-[200px]">Item Name</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600 w-[120px]">Type</th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-600 w-[80px]">Unit</th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[90px] bg-blue-50">US Qty</th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[90px] bg-amber-50">In Bags</th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[100px] bg-green-50">Guatemala</th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[80px]">Total</th>
                            {isColumnVisible('unit_cost') && <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[90px]">Unit Cost</th>}
                            {isColumnVisible('unit_cost') && <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[100px]">Total Value</th>}
                            {isColumnVisible('lot_batch') && <th className="px-4 py-3 text-left font-semibold text-slate-600 w-[120px]">Lot/Batch</th>}
                            {isColumnVisible('expiration_date') && <th className="px-4 py-3 text-left font-semibold text-slate-600 w-[110px]">Expiration</th>}
                            {isColumnVisible('country_of_origin') && <th className="px-4 py-3 text-left font-semibold text-slate-600 w-[80px]">Origin</th>}
                            {isColumnVisible('controlled_substance') && <th className="px-4 py-3 text-center font-semibold text-slate-600 w-[90px]">Controlled</th>}
                            {isColumnVisible('par_level') && <th className="px-4 py-3 text-right font-semibold text-slate-600 w-[80px]">Par</th>}
                            <th className="px-4 py-3 text-center font-semibold text-slate-600 w-[60px]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="10" className="px-4 py-8 text-center text-slate-500">Loading...</td>
                            </tr>
                          ) : items.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="px-4 py-8 text-center text-slate-500">
                                No items yet. Click "Add Item" to create one.
                              </td>
                            </tr>
                          ) : (
                            [...items]
                            .filter(item => {
                              if (controlledFilter === 'controlled') return item.controlled_substance;
                              if (controlledFilter === 'non-controlled') return !item.controlled_substance;
                              return true;
                            })
                            .sort((a, b) => {
                              switch (inventorySort) {
                                case 'name-az': return (a.name || '').localeCompare(b.name || '');
                                case 'name-za': return (b.name || '').localeCompare(a.name || '');
                                case 'us-asc': return (a.qty_us_warehouse || 0) - (b.qty_us_warehouse || 0);
                                case 'us-desc': return (b.qty_us_warehouse || 0) - (a.qty_us_warehouse || 0);
                                case 'gt-asc': return (a.qty_guatemala || 0) - (b.qty_guatemala || 0);
                                case 'gt-desc': return (b.qty_guatemala || 0) - (a.qty_guatemala || 0);
                                case 'exp-asc': return (a.expiration_date || '9999').localeCompare(b.expiration_date || '9999');
                                case 'exp-desc': return (b.expiration_date || '').localeCompare(a.expiration_date || '');
                                default: return 0;
                              }
                            }).map((item, idx) => (
                              <tr key={item.item_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-testid={`item-row-${item.item_id}`}>
                                {/* Name (editable) */}
                                <td className={`px-4 py-2 border-r border-slate-100 ${undoState?.itemId === item.item_id ? 'bg-secondary/20' : ''}`}>
                                  <button
                                    className="text-left hover:text-primary hover:underline underline-offset-2 flex items-center gap-1.5"
                                    onClick={() => openDetail(item)}
                                    data-testid={`item-detail-${item.item_id}`}
                                  >
                                    {item.name}
                                    <Info className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  </button>
                                </td>
                                
                                {/* Type */}
                                <td className="px-4 py-2 border-r border-slate-100">
                                  <Badge variant="outline" className="font-normal">{item.type_name}</Badge>
                                </td>
                                
                                {/* Unit */}
                                <td className="px-4 py-2 border-r border-slate-100 text-slate-500">{item.unit}</td>
                                
                                {/* US Qty (editable) */}
                                <td className={`px-4 py-2 text-right border-r border-slate-100 bg-blue-50/30 ${undoState?.itemId === item.item_id && undoState?.field === 'qty_us_warehouse' ? 'ring-2 ring-secondary ring-inset' : ''}`}>
                                  {editingCell?.itemId === item.item_id && editingCell?.field === 'qty_us_warehouse' ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleKeyDown} className="h-8 w-20 text-sm text-right" autoFocus />
                                      <button onClick={saveEdit} className="p-1 text-green-600"><Check className="w-3 h-3" /></button>
                                    </div>
                                  ) : (
                                    <span className="cursor-pointer hover:bg-blue-100 px-2 py-1 rounded font-medium" onClick={() => startEdit(item.item_id, 'qty_us_warehouse', item.qty_us_warehouse)}>
                                      {item.qty_us_warehouse || 0}
                                    </span>
                                  )}
                                </td>
                                
                                {/* In Bags (read-only) */}
                                <td className="px-4 py-2 text-right border-r border-slate-100 bg-amber-50/30 text-amber-700 font-medium">
                                  {item.qty_in_bags || 0}
                                </td>
                                
                                {/* Guatemala Qty (editable) */}
                                <td className={`px-4 py-2 text-right border-r border-slate-100 bg-green-50/30 ${undoState?.itemId === item.item_id && undoState?.field === 'qty_guatemala' ? 'ring-2 ring-secondary ring-inset' : ''}`}>
                                  {editingCell?.itemId === item.item_id && editingCell?.field === 'qty_guatemala' ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} onKeyDown={handleKeyDown} className="h-8 w-20 text-sm text-right" autoFocus />
                                      <button onClick={saveEdit} className="p-1 text-green-600"><Check className="w-3 h-3" /></button>
                                    </div>
                                  ) : (
                                    <span className="cursor-pointer hover:bg-green-100 px-2 py-1 rounded font-medium text-green-700" onClick={() => startEdit(item.item_id, 'qty_guatemala', item.qty_guatemala)}>
                                      {item.qty_guatemala || 0}
                                    </span>
                                  )}
                                </td>
                                
                                {/* Total with par level indicator */}
                                <td className="px-4 py-2 text-right border-r border-slate-100 font-bold text-slate-700">
                                  {(() => {
                                    const total = (item.qty_us_warehouse || 0) + (item.qty_in_bags || 0) + (item.qty_guatemala || 0);
                                    const par = item.par_level;
                                    const belowPar = par != null && total < par;
                                    return (
                                      <span className={belowPar ? 'text-red-600' : ''} title={par != null ? `Par: ${par}` : ''}>
                                        {total}
                                        {belowPar && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />}
                                      </span>
                                    );
                                  })()}
                                </td>
                                
                                {/* Unit Cost (conditional) */}
                                {isColumnVisible('unit_cost') && (
                                <td className="px-4 py-2 text-right border-r border-slate-100 text-slate-500">
                                  ${(item.unit_cost || 0).toFixed(2)}
                                </td>
                                )}
                                
                                {/* Total Value (conditional — tied to unit_cost) */}
                                {isColumnVisible('unit_cost') && (
                                <td className="px-4 py-2 text-right border-r border-slate-100 font-medium text-slate-600">
                                  ${(((item.qty_us_warehouse || 0) + (item.qty_in_bags || 0) + (item.qty_guatemala || 0)) * (item.unit_cost || 0)).toFixed(2)}
                                </td>
                                )}

                                {/* Optional columns */}
                                {isColumnVisible('lot_batch') && <td className="px-4 py-2 text-left border-r border-slate-100 text-sm text-slate-600">{item.lot_batch || ''}</td>}
                                {isColumnVisible('expiration_date') && (
                                  <td className={`px-4 py-2 text-left border-r border-slate-100 text-sm ${item.expiration_date && new Date(item.expiration_date) < new Date(new Date().setMonth(new Date().getMonth() + 3)) ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
                                    {item.expiration_date || ''}
                                  </td>
                                )}
                                {isColumnVisible('country_of_origin') && <td className="px-4 py-2 text-left border-r border-slate-100 text-sm text-slate-600">{item.country_of_origin || ''}</td>}
                                {isColumnVisible('controlled_substance') && <td className="px-4 py-2 text-center border-r border-slate-100 text-sm">{item.controlled_substance ? <span className="text-red-600 font-medium">Yes</span> : <span className="text-slate-400">No</span>}</td>}
                                {isColumnVisible('par_level') && <td className="px-4 py-2 text-right border-r border-slate-100 text-sm text-slate-600">{item.par_level || ''}</td>}
                                
                                {/* Actions */}
                                <td className="px-4 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    {undoState?.itemId === item.item_id && (
                                      <button onClick={handleUndo} className="p-1 text-amber-600 hover:bg-amber-50 rounded" title="Undo last change" data-testid={`undo-${item.item_id}`}>
                                        <Undo2 className="w-4 h-4" />
                                      </button>
                                    )}
                                    <button onClick={() => handleDeleteItem(item.item_id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`delete-item-${item.item_id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                        {items.length > 0 && (
                          <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                            <tr>
                              <td className="px-4 py-3 font-bold text-slate-700" colSpan="3">Totals</td>
                              <td className="px-4 py-3 text-right font-bold text-blue-700 bg-blue-50">{totals.us.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-bold text-amber-700 bg-amber-50">{totals.inBags.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-bold text-green-700 bg-green-50">{totals.guatemala.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800">{(totals.us + totals.inBags + totals.guatemala).toLocaleString()}</td>
                              {isColumnVisible('unit_cost') && (
                                <>
                                  <td className="px-4 py-3 text-right text-slate-500">—</td>
                                  <td className="px-4 py-3 text-right font-bold text-slate-800">${totals.value.toLocaleString()}</td>
                                </>
                              )}
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>

      {/* Add Type Dialog */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {categoryConfig[activeCategory]?.label} Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Type Name</label>
              <Input
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="e.g., Suture, Bandages, Anesthesia..."
                data-testid="type-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Unit of Measurement</label>
              <Input
                value={typeForm.unit_of_measurement}
                onChange={(e) => setTypeForm({ ...typeForm, unit_of_measurement: e.target.value })}
                placeholder="e.g., box, roll, vial, each..."
                data-testid="type-unit-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTypeDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateType} data-testid="create-type-btn">Create Type</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {categoryConfig[activeCategory]?.label} Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Type</label>
              <Select value={itemForm.type_id} onValueChange={(v) => setItemForm({ ...itemForm, type_id: v })}>
                <SelectTrigger data-testid="item-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {types.map(t => (
                    <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Item Name</label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., 3-0 Vicryl, Gauze 4x4..."
                data-testid="item-name-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">US Qty</label>
                <Input
                  type="number"
                  value={itemForm.qty_us_warehouse}
                  onChange={(e) => setItemForm({ ...itemForm, qty_us_warehouse: parseInt(e.target.value) || 0 })}
                  data-testid="item-us-qty-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Guatemala Qty</label>
                <Input
                  type="number"
                  value={itemForm.qty_guatemala}
                  onChange={(e) => setItemForm({ ...itemForm, qty_guatemala: parseInt(e.target.value) || 0 })}
                  data-testid="item-guatemala-qty-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Unit Cost ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.unit_cost}
                  onChange={(e) => setItemForm({ ...itemForm, unit_cost: parseFloat(e.target.value) || 0 })}
                  data-testid="item-cost-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateItem} disabled={!itemForm.type_id || !itemForm.name} data-testid="create-item-btn">Create Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Detail / Edit Modal */}
      <Dialog open={!!detailItem} onOpenChange={(open) => !open && setDetailItem(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Type</label>
                <Select value={detailForm.type_id} onValueChange={(v) => setDetailForm({ ...detailForm, type_id: v })}>
                  <SelectTrigger data-testid="detail-type-select">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map(t => (
                      <SelectItem key={t.type_id} value={t.type_id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Item Name</label>
                <Input
                  value={detailForm.name}
                  onChange={(e) => setDetailForm({ ...detailForm, name: e.target.value })}
                  data-testid="detail-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Unit of Measurement</label>
                  <Input
                    value={detailForm.unit_of_measurement}
                    onChange={(e) => setDetailForm({ ...detailForm, unit_of_measurement: e.target.value })}
                    placeholder="e.g., box, each, vial"
                    data-testid="detail-unit-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Unit Cost ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={detailForm.unit_cost}
                    onChange={(e) => setDetailForm({ ...detailForm, unit_cost: e.target.value })}
                    data-testid="detail-cost-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Par Level</label>
                <Input
                  type="number"
                  value={detailForm.par_level}
                  onChange={(e) => setDetailForm({ ...detailForm, par_level: e.target.value })}
                  placeholder="Min qty to maintain"
                  data-testid="detail-par-input"
                />
                <p className="text-xs text-slate-400 mt-1">Total below par shows a warning</p>
              </div>

              {/* Lot, Expiration, Origin, Controlled */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Lot / Batch #</label>
                  <Input
                    value={detailForm.lot_batch}
                    onChange={(e) => setDetailForm({ ...detailForm, lot_batch: e.target.value })}
                    placeholder="e.g., LOT-2026-001"
                    data-testid="detail-lot-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Expiration Date</label>
                  <Input
                    type="date"
                    value={detailForm.expiration_date}
                    onChange={(e) => setDetailForm({ ...detailForm, expiration_date: e.target.value })}
                    data-testid="detail-expiration-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Country of Origin</label>
                  <Input
                    value={detailForm.country_of_origin}
                    onChange={(e) => setDetailForm({ ...detailForm, country_of_origin: e.target.value })}
                    placeholder="USA"
                    data-testid="detail-origin-input"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={detailForm.controlled_substance}
                      onChange={(e) => setDetailForm({ ...detailForm, controlled_substance: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                      data-testid="detail-controlled-input"
                    />
                    <span className="text-sm font-medium text-slate-700">Controlled Substance</span>
                  </label>
                </div>
              </div>

              {/* Suppliers */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-700">Suppliers</p>
                <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-primary mb-1.5">Primary Supplier</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={detailForm.supplier_primary_name} onChange={(e) => setDetailForm({ ...detailForm, supplier_primary_name: e.target.value })} placeholder="Supplier name" className="h-8 text-sm" data-testid="detail-supplier-primary-name" />
                      <Input value={detailForm.supplier_primary_pn} onChange={(e) => setDetailForm({ ...detailForm, supplier_primary_pn: e.target.value })} placeholder="Product #" className="h-8 text-sm" data-testid="detail-supplier-primary-pn" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1.5">Secondary Supplier 1</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={detailForm.supplier_secondary1_name} onChange={(e) => setDetailForm({ ...detailForm, supplier_secondary1_name: e.target.value })} placeholder="Supplier name" className="h-8 text-sm" />
                      <Input value={detailForm.supplier_secondary1_pn} onChange={(e) => setDetailForm({ ...detailForm, supplier_secondary1_pn: e.target.value })} placeholder="Product #" className="h-8 text-sm" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1.5">Secondary Supplier 2</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={detailForm.supplier_secondary2_name} onChange={(e) => setDetailForm({ ...detailForm, supplier_secondary2_name: e.target.value })} placeholder="Supplier name" className="h-8 text-sm" />
                      <Input value={detailForm.supplier_secondary2_pn} onChange={(e) => setDetailForm({ ...detailForm, supplier_secondary2_pn: e.target.value })} placeholder="Product #" className="h-8 text-sm" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Read-only quantity summary */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Current Quantities</p>
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div>
                    <p className="font-bold text-blue-700">{detailItem.qty_us_warehouse || 0}</p>
                    <p className="text-xs text-slate-500">US Warehouse</p>
                  </div>
                  <div>
                    <p className="font-bold text-amber-700">{detailItem.qty_in_bags || 0}</p>
                    <p className="text-xs text-slate-500">In Bags</p>
                  </div>
                  <div>
                    <p className="font-bold text-green-700">{detailItem.qty_guatemala || 0}</p>
                    <p className="text-xs text-slate-500">Guatemala</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailItem(null)}>Cancel</Button>
            <Button onClick={saveDetail} disabled={savingDetail || !detailForm.name} className="bg-rojo hover:bg-rojo-dark" data-testid="save-detail-btn">
              {savingDetail ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default InventorySystem;
